import { Flex } from 'antd';
import { FC, useCallback, useState } from 'react';

import { AppMessage, AppModal, CircleSpinnerIcon } from '@/common/components';
import { AppModalControlProps } from '@/common/interfaces';
import { extractFormErrors, messageOnError } from '@/common/utils';

import { SearchConsoleIndexingQueryKeys, usePushIndexingPageMutation } from '@/modules/Domains';

import { SearchConsoleIndexingFormView } from './SearchConsoleIndexingFormView';

interface Props {
  modalControls: AppModalControlProps;
  siteId: string;
  pageId: string;
}

export const SearchConsolePushIndexingContainer: FC<Props> = ({ modalControls, siteId, pageId }) => {
  const appMessageApi = AppMessage.useControls();
  const [domainIds, setDomainIds] = useState<string[]>([]);
  const [errors, setErrors] = useState<Record<string, string> | null>(null);

  const {
    mutate: indexingPageMutation,
    isLoading: isIndexingLoading,
  } = usePushIndexingPageMutation(siteId)({
    onMutate: () => {
      appMessageApi.info(
        <Flex align="center"><CircleSpinnerIcon/>&nbsp;<span>Відправка запиту на індексування...</span></Flex>,
        { key: SearchConsoleIndexingQueryKeys.PushIndexing + siteId }
      );
    },
    onSuccess: () => {
      appMessageApi.success(
        'Запит на індексування відправлено успішно!',
        { key: SearchConsoleIndexingQueryKeys.PushIndexing + siteId }
      );
    },
    onError: (error) => {
      setErrors(extractFormErrors(error));
      messageOnError(error, appMessageApi, { key: SearchConsoleIndexingQueryKeys.PushIndexing + siteId });
    }
  });

  const handleChangeDomainIds = (value: string[]) => {
    setErrors(null);
    setDomainIds(value);
  };

  const handleIndexingPage = useCallback(() => {
    if (!domainIds.length) {
      setErrors({ domainIds: 'Хоча б один домен є обовʼязковим' });
      return;
    }

    handleCloseModal();

    indexingPageMutation({
      domainIds,
      pageId
    });
  }, [domainIds, pageId]);

  const handleCloseModal = useCallback(() => {
    setTimeout(() => {
      setErrors(null);
      setDomainIds([]);
    }, 800);

    modalControls.close();
  }, [modalControls]);

  return (
    <>
      <AppModal
        title="Запит на індексування в Google Search Console"
        width={ 600 }
        footer={ false }
        maskClosable={ false }
        centered
        controls={{
          ...modalControls,
          open: isIndexingLoading ? () => {} : modalControls.open,
          close: handleCloseModal
        }}
      >
        <SearchConsoleIndexingFormView
          domainIds={ domainIds }
          errors={ errors }
          isIndexingPageLoading={ isIndexingLoading }
          siteId={ siteId }
          handleChangeDomainIds={ handleChangeDomainIds }
          handleIndexingPage={ handleIndexingPage }
        />
      </AppModal>
    </>
  );
};
